import Config from 'config/settings';
import { globals } from 'config/globals';
import ComboCounter from 'utils/ComboCounter';
import { device } from 'utils/device';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import ParticleGridPatterns from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import ParticleTargets from 'app/views/home/targets/ParticleTargets';
import {
	BEHAVIOUR_BREATHING,
	BEHAVIOUR_PHYSICS,
	BEHAVIOUR_REPELL,
	FLAG_ACTIVE,
	PHYS_LERPING,
	REPELL_PUSH,
	VEC_X,
	VEC_Y,
	writeVectorConfig
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { FRAME_RING, FRAME_SOLID2, FRAME_SQUARE } from 'app/views/home/webgl/particlesOptimised/core/frames';
import { hexToTint } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { GridPoint } from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import type { ParticleCore } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { SystemsContext } from 'app/views/home/webgl/particlesOptimised/departments/systems/SystemsContext';

/**
 * OptimisedSingleLine — FORK of fields/gridItems/SingleLine.ts over lanes:
 * one travelling head particle (physics lerp between grid targets), endpoint
 * markers (the line's ComboCounter choreography) and the head's
 * distance-mode trail emitter (ParticleTrailEmitter's distance branch — the
 * only mode the systems flow uses; the frames/burst branches have no live
 * caller here). The dead code after `return` in particleTargetReached (the
 * this.test walk) is not ported; the col/row random walk before it IS (its
 * rolls shift shared RNG state in the reference).
 *
 * Reference quirks kept:
 *  - the very FIRST line of the FIRST systems visit lerps at NaN speed
 *    (globals.fastModeScale is undefined until _singleLine sets it AFTER
 *    the delay-0 line already spawned) — a stuck, invisible head; later
 *    visits are fine because fastModeScale persists;
 *  - markers' changeParticle zIndex=100 write is inert in the reference
 *    (sortableChildren = false on the systems container) — not ported;
 *  - SingleLine.maxX/maxY writes land on dead ParticlePhysics fields — not
 *    ported.
 *
 * Trail/emitter timing note: the reference emitter samples its head's
 * position during the particle tick (after that frame's physics); here the
 * line ticks at choreography level BEFORE the kernels, so the emitter sees
 * the head's previous-frame rel position — one frame of sampling lag on the
 * rail dots, invisible (they are static once placed).
 */
export class OptimisedSingleLine {
	public headLane: number = -1;

	private _ctx: SystemsContext;
	private _core: ParticleCore;

	private startTargetId: string = null;
	private endTargetId: string = null;
	private totalParticleCount = 0;
	private totalParticleLimit = 100;
	private particlesPerSpawn = 2;
	private spawnFrames = 5;
	private counter = 0;
	private active: boolean = false;
	private _endTargetGridPoint: GridPoint = { col: 0, row: 0 };
	private _fastMode: boolean = false;

	private col = 10;
	private row = 5;
	private _targetPasses = 5;
	private _passesCount = 0;
	private _minPasses = 3;
	private _maxPasses = 15;
	private _counters: ComboCounter = new ComboCounter();
	private _startCounterActive: boolean = false;

	//Emitter state (ParticleTrailEmitter, distance mode):
	private _emitterActive = false;
	private _lastTrailX = 0;
	private _lastTrailY = 0;
	private _pointAtEachDistance = 0.001;
	private _generateScale: number;
	private _childLifetimeFramesBase = 50;
	private _childLifetimeFramesSeed = 0;
	private _colour = '#ffffff';

	constructor(
		ctx: SystemsContext,
		startGridId: string,
		endGridId: string,
		totalParticleLimit = 100,
		particlesPerSpawn = 2,
		spawnFrames = 5,
		delay = 0
	) {
		this._ctx = ctx;
		this._core = ctx.core;

		this.startTargetId = startGridId;
		this.endTargetId = endGridId;
		this.totalParticleCount = 0;
		this.totalParticleLimit = totalParticleLimit;
		this.spawnFrames = spawnFrames;
		this.particlesPerSpawn = particlesPerSpawn;

		if (!delay) {
			this.next();
			return;
		}

		this.active = true;
		this._counters.clear();
		this._counters.add(this.next.bind(this), delay);
		this._startCounterActive = true;
	}

	public set endTargetGridPoint(targetPosition: GridPoint) {
		this._endTargetGridPoint = targetPosition;
	}

	public clear(): void {
		this._counters.clear();

		if (this.headLane !== -1) {
			//headParticle.pooled = false + deactivate():
			this._ctx.freeLane(this.headLane);
			this._core.stateFlags[this.headLane] &= ~FLAG_ACTIVE;
			this.headLane = -1;
		}
	}

	public tick(): void {
		if (!this.active) {
			return;
		}

		this._counters.tick();

		if (!this._startCounterActive) {
			this.counter -= this._ctx.deps.context.deltaTime;

			if (this.counter <= 0) {
				this.next();
			}
		}

		this._tickEmitter();

		this._processSaveZoneHide();
	}

	/** The physics kernel's lerp completion for this line's head. */
	public headTargetReached(): void {
		this.particleTargetReached();
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	private _processSaveZoneHide(): void {
		if (this.headLane === -1) {
			return;
		}

		const core = this._core;
		const sizes = this._ctx.deps.getSizes();
		const xOffset = this._ctx.deps.sceneProps.mouse.particleContainersX.systems;
		//position.absX = the parallax targetX (the tx lane); absY = relY * container.h:
		const inSafeZone = ParticleGridTargets.inSafeZone(
			xOffset + core.tx[this.headLane],
			core.relY[this.headLane] * sizes.innerHeight
		);

		core.tAlpha[this.headLane] = inSafeZone ? 0.05 : 1;
	}

	private retarget(newGridPoint: string): void {
		let speedVal = this._fastMode ? 100 + Math.random() * 200 : 600 + Math.random() * 600;
		speedVal *= globals.fastModeScale;

		this._startLerpToTarget(this.headLane, newGridPoint, speedVal);
	}

	private next(): void {
		this.active = true;
		this._startCounterActive = false;

		for (let i = 0; i < this.particlesPerSpawn; i++) {
			if (this.totalParticleCount < this.totalParticleLimit) {
				this.spawnParticle();
			} else {
				break; //onLineSpawnComplete is a reference no-op
			}
		}

		this.counter = this.spawnFrames;
	}

	private spawnParticle(): void {
		this._fastMode = false;
		const lane = this._ctx.allocLane();

		if (lane === -1) {
			return;
		}

		const core = this._core;

		this._passesCount = 0;
		this._targetPasses = globals.singleLineMode
			? 1
			: this._minPasses + Math.round(Math.random() * (this._maxPasses - this._minPasses));

		this._ctx.deactivateAll(lane);
		core.stateFlags[lane] |= FLAG_ACTIVE;
		core.behaviourMask[lane] |= BEHAVIOUR_PHYSICS;
		this._spawnAtTarget(lane, this.startTargetId);

		let speedVal = this._fastMode ? 100 + Math.random() * 200 : 700;
		speedVal *= globals.fastModeScale;
		this._startLerpToTarget(lane, this.endTargetId, speedVal);

		//onTargetReached -> the phase's lane routing (headLane).
		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);

		//(maxX/maxY = 0.005 writes land on dead reference fields — skipped.)

		//emitter.active + startDistanceMode(position.x, position.y) +
		//randomiseChildLifetime(420, 30):
		this._emitterActive = true;
		this._lastTrailX = core.relX[lane];
		this._lastTrailY = core.relY[lane];
		this._randomiseChildLifetime(420, 30);

		//setEase({alpha: 5}):
		core.easeAlpha[lane] = 5;

		this.totalParticleCount++;

		this.headLane = lane;

		this.generateTargetMarker(core.lerpEndX[lane], core.lerpEndY[lane]);
	}

	/** ParticlePhysics.spawnAtTarget, lane-for-line. */
	private _spawnAtTarget(lane: number, targetId: string): void {
		const target = ParticleTargets.getTarget(targetId);

		if (!target) {
			return;
		}

		const core = this._core;
		const sizes = this._ctx.deps.getSizes();

		//targetX/Y = 0 (transient), targetScale 0.25, targetAlpha 1:
		core.tx[lane] = 0;
		core.ty[lane] = 0;
		core.tScale[lane] = 0.25;
		core.tAlpha[lane] = 1;

		//screenPxToRelative({x, y}, 'systems'):
		const relX = (target[0] - this._ctx.deps.sceneProps.mouse.particleContainersX.systems) / sizes.virtualWidth;
		const relY = target[1] / sizes.innerHeight;

		//removeLerp():
		core.lerpFrames[lane] = 0;
		core.lerpTargetFrames[lane] = 0;
		core.physFlags[lane] &= ~PHYS_LERPING;

		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);
		this._ctx.setHardPosition(lane, relX, relY);

		//setEase({x: 1, y: 1, scale: 1, alpha: 1}):
		core.easeX[lane] = 1;
		core.easeY[lane] = 1;
		core.easeScale[lane] = 1;
		core.easeAlpha[lane] = 1;
		core.easeLerpFramesX[lane] = 0;
		core.easeLerpFramesY[lane] = 0;
	}

	/** ParticlePhysics.startLerpToTarget + lerpToPosition, lane-for-line
	 *  (NaN speeds propagate exactly like the reference — see the class
	 *  header quirk). */
	private _startLerpToTarget(lane: number, targetId: string, timeMs: number): void {
		const target = ParticleTargets.getTarget(targetId);

		if (!target) {
			console.log('NO TARGET ID', targetId);
			return;
		}

		const core = this._core;
		const sizes = this._ctx.deps.getSizes();

		const relX = (target[0] - this._ctx.deps.sceneProps.mouse.particleContainersX.systems) / sizes.virtualWidth;
		const relY = target[1] / sizes.innerHeight;

		core.lerpFrames[lane] = 0;
		core.lerpTargetFrames[lane] = timeMs / 16;

		core.lerpStartX[lane] = core.relX[lane];
		core.lerpStartY[lane] = core.relY[lane];

		core.lerpEndX[lane] = relX;
		core.lerpEndY[lane] = relY;

		core.physFlags[lane] |= PHYS_LERPING;

		core.lerpEase[lane] = Math.random() < 0.5 ? 1 : 0;
	}

	public generateTargetMarker(relX: number, relY: number): void {
		this.generateMarker(relX, relY, 0.1 + Math.random() * 0.45);
	}

	private generateMarker(relX: number, relY: number, scale: number): void {
		const lane = this._ctx.allocLane();

		if (lane === -1) {
			return;
		}

		const core = this._core;

		this._ctx.deactivateAll(lane);
		core.stateFlags[lane] |= FLAG_ACTIVE;
		this._ctx.setHardPosition(lane, relX, relY);
		writeVectorConfig(core, lane, VEC_X, false);
		writeVectorConfig(core, lane, VEC_Y, false);

		//setEase({scale: 20, alpha: 20}):
		core.easeScale[lane] = 20;
		core.easeAlpha[lane] = 20;

		//changeParticle(null, true): the shape roll (the zIndex write is
		//inert in the reference — sortableChildren false — skipped):
		const ran = Math.random();
		let frame = FRAME_RING;
		if (ran < 0.2) frame = FRAME_SQUARE;
		else if (ran < 0.4) frame = FRAME_SOLID2;
		core.textureId[lane] = frame;

		const isSquare = frame === FRAME_SQUARE;
		if (isSquare && scale > 0.4) {
			scale *= 0.55;
		}

		if (device.innerWidth < Config.mobileBreakpoint) {
			scale *= 1.4;
		}

		core.scale[lane] = 0;
		core.tScale[lane] = scale;
		core.alpha[lane] = 0;
		core.tAlpha[lane] = 1;

		const colour = Math.random() < 0.3 ? '#ffffff' : globals.accentColour;
		const railColour = Math.random() < 0.5 ? '#ffffff' : colour;

		//headParticle.emitter.setColour(railColour):
		this._colour = railColour;

		core.tint[lane] = hexToTint(colour);

		//stopBreathing():
		core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;

		const isMobile = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

		this._counters.add(() => {
			writeVectorConfig(core, lane, VEC_X, true);
			writeVectorConfig(core, lane, VEC_Y, true);

			//repell: systemsMouse, PUSH 50 (the shadowing isMobile re-read is
			//local to the breathing gate in the reference):
			const isMobileInner = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

			core.behaviourMask[lane] |= BEHAVIOUR_REPELL;
			core.repellType[lane] = REPELL_PUSH;
			core.repellRadius[lane] = 50;
			core.repellPower[lane] = 30 + Math.random() * 200;

			if (!isMobileInner && !isSquare && Math.random() < 0.75) {
				//startBreathing(scale, 0.02*scale, 0.8*scale, [-1,1],
				//rand*0.4*scale, ±1):
				const startChangeValue = Math.random() * 0.4 * scale;
				const startDirection = Math.random() < 0.5 ? -1 : 1;
				core.breathBase[lane] = scale;
				core.breathChange[lane] = 0.02 * scale;
				core.breathMax[lane] = 0.8 * scale;
				core.breathAdd[lane] = startChangeValue * startDirection;
				core.breathDir[lane] = startDirection;
				core.behaviourMask[lane] |= BEHAVIOUR_BREATHING;
			}
		}, 1000);

		this._counters.add(
			() => {
				core.easeScale[lane] = 10 + Math.random() * 40;
				core.easeAlpha[lane] = 40;
				core.tAlpha[lane] = 0;
				core.tScale[lane] = 0;
				core.behaviourMask[lane] &= ~BEHAVIOUR_BREATHING;
			},
			isMobile ? 4000 : 6000
		);

		this._counters.add(
			() => {
				//resetParticle() + pooled = false + deactivate():
				core.textureId[lane] = FRAME_SOLID2;
				this._ctx.freeLane(lane);
				core.stateFlags[lane] &= ~FLAG_ACTIVE;
			},
			isMobile ? 8000 : 10000
		);
	}

	/** SingleLine.particleTargetReached — the live path only (the this.test
	 *  walk after the unconditional return is dead and not ported). */
	private particleTargetReached(): void {
		this._passesCount++;

		//The col/row random walk feeds nothing live but its rolls are kept
		//(shared RNG stream):
		const x = Math.ceil(Math.random() * 2.99) - 2;
		const y = Math.ceil(Math.random() * 2.99) - 2;

		this.col += x;
		this.row += y;

		if (this.col > ParticleGridTargets.cols - 1) this.col = ParticleGridTargets.cols - 1;
		else if (this.col < 1) this.col = 1;

		if (this.row > ParticleGridTargets.rows - 1) this.row = ParticleGridTargets.rows - 1;
		else if (this.row < 1) this.row = 1;

		let target: GridPoint | null;
		let respawn = false;

		if (this._passesCount >= this._targetPasses) {
			respawn = true;
			target = ParticleGridPatterns.getSpawnTarget();
		} else {
			if (globals.rightAngleMode) {
				const minSteps = device.innerWidth <= HomeScreens.switchScreensLayoutAt ? 3 + Math.random() * 3 : 1;
				target = ParticleGridPatterns.getRightAngleTarget(
					this._endTargetGridPoint.col,
					this._endTargetGridPoint.row,
					minSteps + Math.random() * 1
				);
			} else {
				const minSteps = device.innerWidth <= HomeScreens.switchScreensLayoutAt ? 4 : 1;
				target = ParticleGridPatterns.get45DegreeTarget(
					this._endTargetGridPoint.col,
					this._endTargetGridPoint.row,
					minSteps + Math.random() * 1
				);
			}
		}

		if (target) {
			if (!respawn) {
				this.retarget('grid_' + target.col + '_' + target.row);
				this.endTargetGridPoint = target;
			} else {
				const minSteps = globals.singleLineMode ? 1 + Math.random() * 3 : 2 + Math.random() * 7;
				let newGridPoint: GridPoint | null;
				if (globals.rightAngleMode) {
					newGridPoint = ParticleGridPatterns.getRightAngleTarget(target.col, target.row, minSteps);
				} else {
					newGridPoint = ParticleGridPatterns.get45DegreeTarget(target.col, target.row, minSteps);
				}

				this._fastMode = false;

				const upperLimit = device.innerWidth < Config.mobileBreakpoint ? 1000 : 600;
				const lowerLimit = device.innerWidth < Config.mobileBreakpoint ? 300 : 600;
				let speedVal = this._fastMode ? 100 + Math.random() * 200 : lowerLimit + Math.random() * upperLimit;

				speedVal *= globals.fastModeScale;

				this._spawnAtTarget(this.headLane, 'grid_' + target.col + '_' + target.row);

				this._startLerpToTarget(this.headLane, 'grid_' + newGridPoint.col + '_' + newGridPoint.row, speedVal);
				this.endTargetGridPoint = newGridPoint;

				this._passesCount = 0;
				this._targetPasses = globals.singleLineMode
					? 1
					: this._minPasses + Math.round(Math.random() * (this._maxPasses - this._minPasses));

				this._emitterActive = true;
				this._lastTrailX = this._core.relX[this.headLane];
				this._lastTrailY = this._core.relY[this.headLane];
				this._randomiseChildLifetime(420, 30);
			}

			this.generateTargetMarker(this._core.lerpEndX[this.headLane], this._core.lerpEndY[this.headLane]);
		}
	}

	//Emitter (ParticleTrailEmitter, distance mode)
	//-----------------------------------------------------------------------------------------------

	private _randomiseChildLifetime(base: number, seed: number): void {
		this._childLifetimeFramesBase = base;
		this._childLifetimeFramesSeed = seed;

		let mobileFix = 2.7;
		let mobileScaleFix = 1.3;
		if (Math.random() < 0.2) {
			this._pointAtEachDistance = Math.random() < 0.4 ? 0.004 : 0.002;
			this._generateScale = this._pointAtEachDistance == 0.004 ? 0.9 : 0.65;

			mobileFix = 1.2;
			mobileScaleFix = 1.2;
		} else {
			this._pointAtEachDistance = 0.0008;
			this._generateScale = 0.4;
		}

		if (device.innerWidth < Config.mobileBreakpoint) {
			this._pointAtEachDistance *= mobileFix;
			this._generateScale *= mobileScaleFix;
		}
	}

	/** ParticleTrailEmitter.tick's distance branch (the head's previous-frame
	 *  rel position — see the class header's timing note). */
	private _tickEmitter(): void {
		if (!this._emitterActive || this.headLane === -1) {
			return;
		}

		const core = this._core;
		const sizes = this._ctx.deps.getSizes();
		const xOffset = this._ctx.deps.sceneProps.mouse.particleContainersX.systems;
		const inSafeZone = ParticleGridTargets.inSafeZone(
			xOffset + core.tx[this.headLane],
			core.relY[this.headLane] * sizes.innerHeight - globals.yGrid
		);

		const yRatio = sizes.innerHeight / sizes.virtualWidth;

		const currentX = core.relX[this.headLane];
		const currentY = core.relY[this.headLane];
		const distance = Math.hypot(currentX - this._lastTrailX, currentY * yRatio - this._lastTrailY * yRatio);

		if (Math.abs(distance) > this._pointAtEachDistance) {
			const xDiff = currentX - this._lastTrailX;
			const yDiff = currentY - this._lastTrailY;
			const xDiffRatio = xDiff / distance;
			const yDiffRatio = yDiff / distance;
			const xStep = this._pointAtEachDistance * xDiffRatio;
			const yStep = this._pointAtEachDistance * yDiffRatio;

			let trackDistance = 0;
			let x = this._lastTrailX;
			let y = this._lastTrailY;

			while (Math.abs(distance) - trackDistance >= this._pointAtEachDistance) {
				const child = this._ctx.allocLane();

				x += xStep;
				y += yStep;

				if (child !== -1) {
					this._ctx.activateAsTrail(
						child,
						this.headLane,
						this._childLifetimeFramesBase,
						this._childLifetimeFramesSeed,
						x,
						y,
						inSafeZone,
						this._generateScale,
						this._colour
					);
				}

				trackDistance += this._pointAtEachDistance;
			}

			this._lastTrailX = x;
			this._lastTrailY = y;
		}
	}
}
